import type { AppointmentFormValues } from '@/types/appointment-form';
import type {
  AppointmentResponse,
  ParticipantResponse,
} from '@/types/scheduling-api';
import type { OrgLocation } from '@/auth/auth-api';

/**
 * Build the `CreateAppointmentPanel` pre-fill for the **popup** follow-up
 * action (PROD-987). Mirrors Flutter `calendar_tab.dart:2306-2307`
 * (`bookFollowUpAppointment` → `onSelect(appointment, context, false, true)`
 * → `calendar_screen.dart:257-271` preloads patient/provider from the
 * fulfilled appointment into a NEW draft).
 *
 * This is the `AppointmentResponse`-sourced twin of
 * `buildFollowUpInitialFromRow` (which seeds from a `DayListRow` for the
 * calendar row-action follow-up). It produces the **same minimal seed
 * shape** so both follow-up entry points behave identically:
 *   - patient context (id + name + photo + dob; gender/age/importance are
 *     available on the richer preload shape so we carry them too — they're
 *     re-hydrated by the PreBook chain regardless, matching
 *     `build-create-appointment-prefill.ts`).
 *   - provider context (id + name + speciality).
 *   - location seeded from the user's first org location (Flutter
 *     `calendar_screen.dart:180`), GUID-guarded by the caller.
 *   - reasonCode carried forward as the seed (Flutter `calendar_screen.dart:170`).
 *   - `referenceAppointmentId = appt.id` — THE signal that locks the
 *     profile picker to FOLLOWUP-type profiles (see the FOLLOWUP-LOCK
 *     contract in `build-follow-up-from-row.ts`).
 *   - `selectedDate` = the calendar's selected day (caller passes it); the
 *     user picks the actual future day for the follow-up in the panel.
 *
 * Deliberately does NOT carry the source appointment's slot, profile,
 * sponsor, recurrence, flags, or comment — a follow-up is a fresh
 * appointment on a FOLLOWUP-type profile, not an edit of the original.
 * (Contrast `appointment-to-form-values.ts`, the EDIT hydrator, which
 * seeds all of those.)
 */

function findParticipant(
  participant: ParticipantResponse[] | undefined,
  type: 'provider' | 'patient',
): ParticipantResponse | undefined {
  return participant?.find((p) => p.referenceType?.toLowerCase() === type);
}

function toLocalDateISO(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Pick the date-strip seed for a follow-up (PR #425 review §5). A follow-up
 * is forward-looking — but the calendar's selected day may be in the PAST
 * (you navigated back to a fulfilled appointment that's days old). Seeding
 * the panel with a past day lands the slot picker on a day with no
 * availability. So clamp: never seed a date before today; when the calendar
 * is already on today/future, keep it (the common case — you book a
 * follow-up while viewing today). `now` is injected for testability.
 *
 * Shared by BOTH follow-up entry points (popup → scheduling-page, row →
 * frame-scheduling) so they don't drift — the row builder
 * (`build-follow-up-from-row.ts`) carries a "audit for parallel drift" note.
 */
export function followUpSeedDateISO(calendarSelected: Date, now: Date): string {
  const sel = new Date(calendarSelected);
  sel.setHours(0, 0, 0, 0);
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  return toLocalDateISO(sel < today ? today : calendarSelected);
}

export function buildFollowUpInitialFromAppointment(
  appt: AppointmentResponse,
  firstLocation: OrgLocation | null,
  selectedDateISO: string,
): Partial<AppointmentFormValues> {
  // Empty-GUID refusal — same guard as `build-follow-up-from-row.ts`. An
  // org location id is a backend GUID; a truthy-but-empty/"0" id makes the
  // LocationSelector skip its `if (!selected)` auto-pick and land the user
  // on a non-pickable location that 400s on Save. The caller is expected
  // to pre-filter (`organizationLocations[0]?.id ? ... : null`); if a
  // truthy-but-empty one still arrives, drop it to null and warn.
  const locId = firstLocation?.id;
  const locationHasRealId =
    typeof locId === 'string' && locId.trim().length > 0 && locId.trim() !== '0';
  if (firstLocation && !locationHasRealId) {
    // eslint-disable-next-line no-console
    console.warn(
      '[build-follow-up-from-appointment] firstLocation passed without a real id; refusing to seed an empty-GUID location. Caller is expected to pre-filter.',
      firstLocation,
    );
  }

  const provider = findParticipant(appt.participant, 'provider')?.provider as
    | { id?: string; name?: string }
    | undefined;
  const patient = findParticipant(appt.participant, 'patient')?.patient;
  const patientLoose = patient as
    | {
        middleName?: string;
        photoUrl?: string;
        dateOfBirth?: string;
        birthDate?: string;
        gender?: string;
        age?: string;
        importance?: { code?: string };
      }
    | undefined;

  const patientName =
    [patient?.firstName, patientLoose?.middleName, patient?.lastName]
      .map((s) => s?.trim() ?? '')
      .filter((s) => s.length > 0)
      .join(' ') || null;

  return {
    location: locationHasRealId
      ? { id: firstLocation!.id, name: firstLocation!.name ?? '' }
      : null,

    providerId: provider?.id ?? null,
    providerName: provider?.name ?? null,
    providerSpeciality: (appt.specialty?.display as string | undefined) ?? null,

    patientId: patient?.id ?? null,
    patientName,
    patientPhoto: patientLoose?.photoUrl ?? null,
    patientDob: patientLoose?.dateOfBirth ?? patientLoose?.birthDate ?? null,
    patientAge: patientLoose?.age ?? null,
    patientGender: patientLoose?.gender ?? null,
    patientImportanceCode: patientLoose?.importance?.code ?? null,

    // Carry the source RFV forward as the seed; the picker overwrites it if
    // the user picks a profile that bundles its own reasonCode (Flutter
    // `calendar_screen.dart:170`).
    reasonCode: appt.reasonCode
      ? {
          code: appt.reasonCode.code,
          display: appt.reasonCode.display,
          definition: appt.reasonCode.definition ?? '',
        }
      : null,

    // FOLLOWUP-LOCK — see build-follow-up-from-row.ts for the full contract.
    // Non-null `referenceAppointmentId` locks the profile picker to
    // FOLLOWUP-type profiles only and satisfies the schema's
    // FOLLOWUP-requires-referenceAppointmentId rule.
    referenceAppointmentId: appt.id,

    selectedDate: selectedDateISO,
  };
}
